import { ConnectError } from '@connectrpc/connect';
import { Box, Button, Container, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useRouteError } from 'react-router';
import { Helmet } from '@dr.pogodin/react-helmet';
import { createRoutes } from '~/routes';
import { useLogError } from '~/pages/ErrorPage/hooks/useLogError';
import { useCloseSession } from '~/shared/hooks/useCloseSession';

function isExpiredAuthTokenError(error: unknown): boolean {
  return (
    error instanceof Error && error.message.includes('auth/user-token-expired')
  );
}

export default function ErrorPage() {
  const error = useRouteError();
  const { t } = useTranslation();
  const closeSession = useCloseSession();
  const expiredAuthToken = isExpiredAuthTokenError(error);

  useEffect(() => {
    if (expiredAuthToken) {
      closeSession();
    }
  }, [expiredAuthToken, closeSession]);

  useLogError(expiredAuthToken ? undefined : error);

  if (expiredAuthToken) {
    return null;
  }

  const errorMessage =
    error instanceof ConnectError ? error.rawMessage : t('errorPage.message');

  return (
    <Box
      sx={{
        py: 8,
      }}
    >
      <Helmet>
        <title>{t('errorPage.title')}</title>
      </Helmet>
      <Container maxWidth="lg">
        <Typography
          component="h1"
          variant="h4"
          sx={{
            color: 'inherit',
            fontWeight: 'bold',
            mb: 4,
          }}
        >
          Oops!
        </Typography>
        <Typography
          component="h2"
          variant="h6"
          sx={{
            color: 'inherit',
            fontWeight: 'bold',
            mb: 8,
          }}
        >
          {errorMessage}
        </Typography>

        <Button
          component={RouterLink}
          variant="contained"
          to={createRoutes.Devices}
          reloadDocument
        >
          {t('errorPage.navigateHome')}
        </Button>
      </Container>
    </Box>
  );
}
